import styled, { type CSSProperties } from 'styled-components/macro'

import { Select, type SelectProps } from '@loadsmart/loadsmart-ui'
import { Field, Layout, Text } from '@loadsmart/miranda-react'

import { type Option } from '../../domain/Option'
import { RequestStatus } from '../../domain/Request'
import { toArray } from '../../utils/array'
import { type ShipmentTag } from '../domain/ShipmentTag'

const StyledField = styled(Field)`
  min-width: unset;
`

export type ShipmentTagValue = Option<ShipmentTag['uuid']>

export type ShipmentTagSelectProps = Omit<SelectProps, 'value' | 'onChange' | 'multiple'> & {
  label: string
  style?: CSSProperties
  status: RequestStatus
  options: Array<Option<ShipmentTag['uuid']>>
  value?: Array<ShipmentTagValue>
  onChange?: (value?: Array<ShipmentTagValue>) => void
}

export function ShipmentTagSelect({
  label,
  style,
  status,
  options,
  value,
  id = 'shipment-tags',
  onChange,
  ...selectProps
}: ShipmentTagSelectProps) {
  function handleOnChange(event: Parameters<NonNullable<SelectProps['onChange']>>[0]) {
    const optionValues = toArray(event?.target?.value as Array<Option> | Option | null)

    if (optionValues.length === 0) {
      onChange?.(undefined)
      return
    }

    onChange?.(optionValues)
  }

  const isLoading = status === RequestStatus.Initial || status === RequestStatus.Pending
  const hasError = status === RequestStatus.Error

  return (
    <Layout.Stack gap="spacing-1" style={style}>
      <StyledField>
        {label && <Field.Label id={id}>{label}</Field.Label>}
        <Select
          id={id}
          options={options}
          value={value}
          multiple
          onChange={handleOnChange}
          disabled={isLoading || selectProps.disabled}
          {...selectProps}
        />
      </StyledField>
      {hasError && (
        <Text variant="body-sm" color="color-text-error">
          Failed to load tags. Please reload the page to try again.
        </Text>
      )}
    </Layout.Stack>
  )
}
